import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ClipboardEvent } from "react";

import { ALFRED_ATTACHMENTS_PER_TICKET_MAX } from "../lib/limits.ts";

import { ALFRED_KEY, alfredErrorOf, uploadAttachment } from "./alfred.ts";
import { checkFiles } from "./attachment-rules.ts";
import { filesOfClipboard, namedPaste } from "./paste.ts";

/** Dónde se cuelga un adjunto: el pedido, o uno de sus comentarios. */
export interface AttachmentTarget {
  readonly ticketId: string;
  readonly commentId?: string;
}

export type PendingState = "ready" | "uploading" | "uploaded" | "failed";

/** Un archivo elegido o pegado que aún no es un adjunto del pedido. */
export interface PendingFile {
  readonly key: string;
  readonly file: File;
  /** La miniatura de una imagen, mientras se ve aquí. */
  readonly preview: string | null;
  readonly state: PendingState;
  readonly error: string | null;
}

/** `URL.createObjectURL` no existe en todas partes (jsdom, por ejemplo). */
const previewOf = (file: File): string | null =>
  file.type.startsWith("image/") && typeof URL.createObjectURL === "function"
    ? URL.createObjectURL(file)
    : null;

const release = (item: PendingFile) => {
  if (item.preview !== null) URL.revokeObjectURL(item.preview);
};

/**
 * Los archivos por subir de UN formulario: el diálogo, un comentario, la
 * edición del pedido. Se agregan --por el selector o pegando-- y se quitan
 * antes de enviar; al enviar se suben uno a uno, en el orden en que se
 * agregaron, y cada uno dice cómo le fue. Lo que ya subió no se vuelve a
 * subir al reintentar.
 *
 * Las miniaturas son `blob:` del navegador: se sueltan al quitar el archivo,
 * al vaciar y al desmontar.
 *
 * Lo que el API no aceptaría --el tipo, el tamaño, pasarse de los adjuntos
 * de un pedido contando los `existing`-- no se agrega, y se dice por qué
 * (`rejected`, hasta el siguiente intento de agregar).
 */
export const usePendingFiles = ({
  existing = 0,
}: { existing?: number } = {}) => {
  const [items, setItems] = useState<readonly PendingFile[]>([]);
  const [rejected, setRejected] = useState<readonly string[]>([]);
  /* Lo último, para subir sin esperar a un render: `upload` cambia el
     estado de cada uno mientras recorre la lista. */
  const current = useRef(items);
  current.current = items;

  useEffect(
    () => () => {
      current.current.forEach(release);
    },
    [],
  );

  const patch = (key: string, change: Partial<PendingFile>) => {
    setItems((list) =>
      list.map((item) => (item.key === key ? { ...item, ...change } : item)),
    );
  };

  const add = (incoming: readonly File[]) => {
    if (incoming.length === 0) return;
    const waiting = current.current.filter(
      (item) => item.state !== "uploaded",
    ).length;
    const { accepted: files, rejected: reasons } = checkFiles(
      incoming,
      ALFRED_ATTACHMENTS_PER_TICKET_MAX - existing - waiting,
    );
    setRejected(reasons);
    if (files.length === 0) return;
    setItems((list) => [
      ...list,
      ...files.map((file) => ({
        key: crypto.randomUUID(),
        file,
        preview: previewOf(file),
        state: "ready" as const,
        error: null,
      })),
    ]);
  };

  const remove = (key: string) => {
    const item = current.current.find((candidate) => candidate.key === key);
    if (item === undefined || item.state === "uploading") return;
    release(item);
    setItems((list) => list.filter((candidate) => candidate.key !== key));
  };

  const clear = () => {
    current.current.forEach(release);
    current.current = [];
    setItems([]);
    setRejected([]);
  };

  /** Sube lo que falta. Devuelve cuántos no se subieron. */
  const upload = async (target: AttachmentTarget): Promise<number> => {
    let failed = 0;
    for (const item of current.current) {
      if (item.state === "uploaded") continue;
      patch(item.key, { state: "uploading", error: null });
      try {
        await uploadAttachment(target, item.file);
        patch(item.key, { state: "uploaded" });
      } catch (error) {
        failed += 1;
        patch(item.key, { state: "failed", error: alfredErrorOf(error) });
      }
    }
    return failed;
  };

  /**
   * El `onPaste` del formulario: si lo pegado trae archivos, se adjuntan y
   * no se pega nada más --ni el nombre del archivo ni el HTML de la imagen--.
   * Si es solo texto, se pega como siempre.
   */
  const onPaste = (event: ClipboardEvent) => {
    const files = filesOfClipboard(event.clipboardData);
    if (files.length === 0) return;
    event.preventDefault();
    add(namedPaste(files, new Date()));
  };

  return {
    items,
    rejected,
    add,
    remove,
    clear,
    upload,
    onPaste,
    /** Los que aún no son adjuntos: por subir o fallidos. */
    unsent: items.filter((item) => item.state !== "uploaded").length,
    failed: items.filter((item) => item.state === "failed").length,
  };
};

export type PendingFiles = ReturnType<typeof usePendingFiles>;

/**
 * Guardar algo con adjuntos: primero lo escrito --el pedido, el
 * comentario, la edición-- y después, colgados de él, los adjuntos.
 *
 * Si un adjunto no sube, lo escrito ya está guardado: no se vuelve a
 * guardar. El formulario se queda como está, con lo escrito a la vista, y
 * volver a enviar solo sube lo que falta. Quitar los que fallaron y enviar
 * termina. `onDone` llega cuando no queda nada por subir.
 */
export const useAttachedSave = <TInput>({
  save,
  onDone,
  existing = 0,
}: {
  save: (input: TInput) => Promise<AttachmentTarget>;
  onDone: (target: AttachmentTarget) => void;
  /** Los adjuntos que ya tiene el pedido, para el tope por pedido. */
  existing?: number;
}) => {
  const files = usePendingFiles({ existing });
  const [saved, setSaved] = useState<AttachmentTarget | null>(null);
  const queryClient = useQueryClient();

  const done = (target: AttachmentTarget) => {
    setSaved(null);
    files.clear();
    onDone(target);
  };

  const mutation = useMutation({
    mutationFn: async (input: TInput) => {
      const target = saved ?? (await save(input));
      setSaved(target);
      return { target, failed: await files.upload(target) };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALFRED_KEY });
    },
    onSuccess: ({ target, failed }) => {
      if (failed === 0) done(target);
    },
  });

  const submit = (input: TInput) => {
    if (mutation.isPending) return;
    if (saved !== null && files.unsent === 0) {
      done(saved);
      return;
    }
    mutation.mutate(input);
  };

  /** Olvidar lo guardado y los archivos: al cerrar o cancelar. */
  const reset = () => {
    setSaved(null);
    files.clear();
    mutation.reset();
  };

  /** Olvidar el fallo de guardar, sin tocar lo adjuntado. */
  const dismiss = () => {
    mutation.reset();
  };

  return {
    files,
    dismiss,
    /** Lo escrito ya se guardó: lo que queda son adjuntos. */
    saved: saved !== null,
    submit,
    reset,
    pending: mutation.isPending,
    /** El fallo de guardar lo escrito; los de los adjuntos van en cada uno. */
    error: mutation.isError ? alfredErrorOf(mutation.error) : null,
  };
};
